import CountryMultiSelect from '@/pages/creators/discovery/_components/filters/CountryMultiSelect';
import {
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValueText,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import type { createListCollection } from '@chakra-ui/react';
import { Box, HStack, SimpleGrid, Text } from '@chakra-ui/react';
import {
  ALLOWED_SHIPPING_COUNTRIES_UPPER,
  type MentionBoostTargetAgeRangeDto,
  type MentionBoostTargetLocationDto,
} from '@fluenceur/types';
import { SparksSolid } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { BOOST_SETUP_FIELD_HEIGHT } from './useMentionBoostSetupForm';
import { SetupFieldLabel } from './SetupSection';
import { buildTargetLocation } from './utils';

// ---- AdvantagePlusControl ----

interface AdvantagePlusControlProps {
  checked: boolean;
  helper: string;
  label: string;
  onChange: (event: { checked: boolean | 'indeterminate' }) => void;
}

export function AdvantagePlusControl({
  checked,
  helper,
  label,
  onChange,
}: AdvantagePlusControlProps) {
  return (
    <Box
      rounded="lg"
      border="1px solid"
      borderColor={checked ? 'blue.500' : 'gray.200'}
      bg={checked ? 'blue.50' : 'white'}
      px={3}
      py={2.5}
      transition="background 160ms ease, border-color 160ms ease"
    >
      <HStack justify="space-between" align="flex-start" gap={3}>
        <HStack gap={2} minW={0} align="flex-start">
          <Box color={checked ? 'blue.500' : 'gray.500'} flexShrink={0} mt={0.5}>
            <SparksSolid width={16} height={16} aria-hidden="true" />
          </Box>
          <Box minW={0}>
            <Text fontSize="sm" fontWeight="semibold" color="gray.900">
              {label}
            </Text>
            <Text fontSize="xs" color="gray.500" mt={0.5} lineHeight="1.4">
              {helper}
            </Text>
          </Box>
        </HStack>
        <Switch
          checked={checked}
          onCheckedChange={onChange}
          colorPalette="blue"
          size="sm"
          flexShrink={0}
        />
      </HStack>
    </Box>
  );
}

// ---- TargetLocationSelector ----

interface TargetLocationSelectorProps {
  label: string;
  onChange: (locations: MentionBoostTargetLocationDto[]) => void;
  value: MentionBoostTargetLocationDto[];
}

export function TargetLocationSelector({ label, onChange, value }: TargetLocationSelectorProps) {
  const { i18n } = useTranslation();
  const countryCodes = value
    .map((location) => location.countryCode)
    .filter((code): code is string => Boolean(code));

  return (
    <Box>
      <SetupFieldLabel>{label}</SetupFieldLabel>
      <CountryMultiSelect
        value={countryCodes}
        allowedCodes={ALLOWED_SHIPPING_COUNTRIES_UPPER}
        onChange={(codes) =>
          onChange((codes ?? []).map((code) => buildTargetLocation(code, i18n.language)))
        }
      />
    </Box>
  );
}

// ---- MinimumAgeSelector ----

interface MinimumAgeSelectorProps {
  collection: ReturnType<typeof createListCollection<{ label: string; value: string }>>;
  label: string;
  onChange: (value: string[]) => void;
  value: number;
}

export function MinimumAgeSelector({
  collection,
  label,
  onChange,
  value,
}: MinimumAgeSelectorProps) {
  return (
    <Box>
      <SetupFieldLabel>{label}</SetupFieldLabel>
      <SelectRoot
        collection={collection}
        value={[String(value)]}
        onValueChange={(details) => onChange(details.value)}
      >
        <SelectTrigger h={BOOST_SETUP_FIELD_HEIGHT}>
          <SelectValueText />
        </SelectTrigger>
        <SelectContent>
          {collection.items.map((item) => (
            <SelectItem key={item.value} item={item}>
              {item.label}
            </SelectItem>
          ))}
        </SelectContent>
      </SelectRoot>
    </Box>
  );
}

// ---- AgeRangeSelector ----

interface AgeRangeSelectorProps {
  collection: ReturnType<typeof createListCollection<{ label: string; value: string }>>;
  label: string;
  maxValue: number;
  minValue: number;
  onMaxChange: (value: string[]) => void;
  onMinChange: (value: string[]) => void;
}

export function AgeRangeSelector({
  collection,
  label,
  maxValue,
  minValue,
  onMaxChange,
  onMinChange,
}: AgeRangeSelectorProps) {
  return (
    <Box>
      <SetupFieldLabel>{label}</SetupFieldLabel>
      <SimpleGrid columns={2} gap={3}>
        <SelectRoot
          collection={collection}
          value={[String(minValue)]}
          onValueChange={(details) => onMinChange(details.value)}
        >
          <SelectTrigger h={BOOST_SETUP_FIELD_HEIGHT}>
            <SelectValueText />
          </SelectTrigger>
          <SelectContent>
            {collection.items.map((item) => (
              <SelectItem key={item.value} item={item}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
        <SelectRoot
          collection={collection}
          value={[String(maxValue)]}
          onValueChange={(details) => onMaxChange(details.value)}
        >
          <SelectTrigger h={BOOST_SETUP_FIELD_HEIGHT}>
            <SelectValueText />
          </SelectTrigger>
          <SelectContent>
            {collection.items.map((item) => (
              <SelectItem key={item.value} item={item}>
                {item.label}
              </SelectItem>
            ))}
          </SelectContent>
        </SelectRoot>
      </SimpleGrid>
    </Box>
  );
}

// ---- AudienceControls (combined section) ----

interface AudienceControlsProps {
  advantagePlusEnabled: boolean;
  advantagePlusAgeCollection: ReturnType<
    typeof createListCollection<{ label: string; value: string }>
  >;
  advantagePlusMinimumAgeInvalid: boolean;
  ageCollection: ReturnType<typeof createListCollection<{ label: string; value: string }>>;
  advantagePlusHelperText: string;
  advantagePlusMinimumAgeError: string;
  advantagePlusLabel: string;
  ageRangeLabel: string;
  locationsLabel: string;
  minimumAgeLabel: string;
  onAdvantagePlusChange: (event: { checked: boolean | 'indeterminate' }) => void;
  onMaxAgeChange: (value: string[]) => void;
  onMinAgeChange: (value: string[]) => void;
  onTargetLocationsChange: (locations: MentionBoostTargetLocationDto[]) => void;
  targetAgeRange: MentionBoostTargetAgeRangeDto;
  targetLocations: MentionBoostTargetLocationDto[];
}

export function AudienceControls({
  advantagePlusEnabled,
  advantagePlusAgeCollection,
  advantagePlusMinimumAgeInvalid,
  ageCollection,
  advantagePlusHelperText,
  advantagePlusMinimumAgeError,
  advantagePlusLabel,
  ageRangeLabel,
  locationsLabel,
  minimumAgeLabel,
  onAdvantagePlusChange,
  onMaxAgeChange,
  onMinAgeChange,
  onTargetLocationsChange,
  targetAgeRange,
  targetLocations,
}: AudienceControlsProps) {
  return (
    <>
      <AdvantagePlusControl
        checked={advantagePlusEnabled}
        helper={advantagePlusHelperText}
        label={advantagePlusLabel}
        onChange={onAdvantagePlusChange}
      />
      <TargetLocationSelector
        label={locationsLabel}
        onChange={onTargetLocationsChange}
        value={targetLocations}
      />
      {advantagePlusEnabled ? (
        <MinimumAgeSelector
          collection={advantagePlusAgeCollection}
          label={minimumAgeLabel}
          value={targetAgeRange.min}
          onChange={onMinAgeChange}
        />
      ) : (
        <AgeRangeSelector
          collection={ageCollection}
          label={ageRangeLabel}
          maxValue={targetAgeRange.max}
          minValue={targetAgeRange.min}
          onMaxChange={onMaxAgeChange}
          onMinChange={onMinAgeChange}
        />
      )}
      {advantagePlusEnabled && advantagePlusMinimumAgeInvalid && (
        <Text fontSize="xs" color="red.fg">
          {advantagePlusMinimumAgeError}
        </Text>
      )}
    </>
  );
}
